import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/siteConfig';

const milestones = [
  { year: '2018', title: '平台成立', desc: '九游娱乐正式成立，聚焦在线游戏娱乐领域，首批上线50+款棋牌游戏' },
  { year: '2019', title: '快速成长', desc: '注册用户突破500万，产品线扩展至电子游艺和体育竞技，团队规模翻倍' },
  { year: '2020', title: '行业领先', desc: '用户突破2000万，荣获"年度最具成长力游戏平台"奖项，技术架构全面升级' },
  { year: '2021', title: '多元发展', desc: '推出捕鱼游戏和彩票游戏品类，移动端全面上线，月活用户突破500万' },
  { year: '2023', title: '品牌升级', desc: '注册用户突破4000万，启动品牌全面升级战略，引入国际顶级游戏供应商' },
  { year: '2025', title: '行业标杆', desc: '用户突破5000万，荣获"年度最佳游戏平台"大奖，成为行业标杆企业' },
];

const faqs = [
  {
    q: '九游娱乐平台是否安全可靠？',
    a: '九游娱乐平台自2018年成立以来，始终将用户安全放在首位。我们采用国际领先的SSL加密技术保护用户数据，所有游戏均经过第三方权威机构认证，确保公平公正。平台已安全稳定运营多年，服务超过5000万用户，是您值得信赖的娱乐平台。',
  },
  {
    q: '如何注册九游娱乐账号？',
    a: '注册非常简单，您只需在九游娱乐官网点击"立即注册"按钮，填写手机号码、设置密码，完成短信验证即可。整个注册过程不超过1分钟。新用户注册还可领取188元专属礼包。',
  },
  {
    q: '九游娱乐支持哪些支付方式？',
    a: '九游娱乐支持多种充值方式，包括微信支付、支付宝、银联在线、QQ钱包等主流支付渠道。充值实时到账，方便快捷。我们采用银行级别的安全加密技术，保障您的资金安全。',
  },
  {
    q: '提现需要多长时间？',
    a: '九游娱乐提供快速提现服务，工作日提现审核通常在5-30分钟内完成，最快可实现秒到账。提现金额不设上限，满足各类用户需求。',
  },
  {
    q: '九游娱乐有手机版吗？',
    a: '有的！九游娱乐已全面支持移动端。安卓用户可前往官网下载APP安装包，iOS用户可在App Store搜索"九游娱乐"下载。手机版与电脑版数据互通，让您随时随地畅享游戏乐趣。',
  },
  {
    q: '遇到问题如何联系客服？',
    a: '九游娱乐提供7×24小时在线客服服务。您可以通过以下方式联系我们：在线客服（官网右下角）、客服热线400-888-9999、官方邮箱service@9game.com。我们承诺3分钟内响应您的问题。',
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于我们 - ${siteConfig.name}`,
    description: `了解${siteConfig.name}的品牌故事、发展历程、行业信誉及联系方式`,
    url: `${siteConfig.url}/about`,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <header>
        <Navbar />
      </header>

      <main className="min-h-screen pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8 md:p-12">
              <div className="text-center mb-10">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-2xl bg-primary-500">
                  <i className="ri-gamepad-fill text-background-50 text-2xl"></i>
                </div>
                <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-3">关于九游娱乐</h1>
                <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                  {siteConfig.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <h3 className="flex items-center gap-2 text-lg font-medium text-foreground-900 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-eye-line text-primary-600"></i>
                    </div>
                    品牌愿景
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    九游娱乐致力于成为全球领先的游戏娱乐平台，为用户提供安全、公平、优质的游戏娱乐体验。我们汇聚全球顶级游戏资源，持续创新产品和服务，让每一位玩家都能在这里找到属于自己的娱乐方式。
                  </p>
                </div>
                <div className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                  <h3 className="flex items-center gap-2 text-lg font-medium text-foreground-900 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                      <i className="ri-heart-line text-accent-600"></i>
                    </div>
                    核心价值观
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    用户至上：一切以用户体验为中心，持续优化产品和服务。诚信经营：坚持公平公正，打造值得信赖的游戏环境。创新驱动：不断探索新技术、新玩法，引领行业发展。
                  </p>
                </div>
              </div>

              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">发展历程</h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-primary-200 -translate-x-1/2 hidden md:block"></div>
                <div className="flex flex-col gap-6">
                  {milestones.map((m, idx) => (
                    <div key={m.year} className={`flex items-start gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                        <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                          <h4 className="text-base font-medium text-foreground-900">{m.title}</h4>
                          <p className="text-xs text-foreground-500 mt-1">{m.desc}</p>
                        </div>
                      </div>
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold z-10">
                        {m.year.slice(2)}
                      </div>
                      <div className="flex-1 md:hidden">
                        <div className="p-4 rounded-xl bg-background-100 border border-background-200/70">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-bold text-primary-600">{m.year}</span>
                            <h4 className="text-base font-medium text-foreground-900">{m.title}</h4>
                          </div>
                          <p className="text-xs text-foreground-500">{m.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">行业信誉</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: 'ri-shield-check-line', title: '安全认证', desc: '国际SSL安全加密' },
                { icon: 'ri-medal-line', title: '行业大奖', desc: '年度最佳游戏平台' },
                { icon: 'ri-user-star-line', title: '用户信赖', desc: '5000万+注册用户' },
                { icon: 'ri-verified-badge-line', title: '公平认证', desc: '第三方权威机构认证' },
              ].map((item) => (
                <div key={item.title} className="flex flex-col items-center gap-2 p-5 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className={`${item.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900">{item.title}</h4>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-14">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 flex-shrink-0">
                  <i className="ri-phone-line text-primary-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">客服热线</h4>
                  <p className="text-sm text-foreground-600 mt-0.5">{siteConfig.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                  <i className="ri-mail-line text-accent-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">官方邮箱</h4>
                  <p className="text-sm text-foreground-600 mt-0.5">{siteConfig.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-secondary-100 flex-shrink-0">
                  <i className="ri-time-line text-secondary-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground-900">服务时间</h4>
                  <p className="text-sm text-foreground-600 mt-0.5">{siteConfig.workingHours}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-950 text-center mb-8">常见问题</h2>
            <div className="max-w-2xl mx-auto flex flex-col gap-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-3 p-5 text-left cursor-pointer"
                  >
                    <span className="text-sm md:text-base font-medium text-foreground-900">{faq.q}</span>
                    <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-foreground-500"></i>
                    </div>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}